// 15-module course content. Each module has sections with title + paragraphs/lists.
export const MODULES = [
  {
    id: 1,
    title: "What is Trading?",
    stage: "Beginner",
    icon: "Coins",
    minutes: 8,
    summary: "The absolute basics: what trading is, how buying and selling works, long vs short, profit and loss.",
    sections: [
      { h: "What is trading?", p: ["Trading is the act of buying and selling financial assets (like stocks, currencies or crypto) with the goal of profiting from changes in their price.", "A trader tries to buy at one price and sell at a different price. The difference between those prices is the profit or the loss."] },
      { h: "Trading vs Investing", list: ["Investing usually means holding an asset for months or years, focusing on long-term growth.", "Trading is typically shorter-term — from minutes to weeks — trying to profit from price movements.", "Neither is 'better'; they are simply different approaches with different time horizons and risks."] },
      { h: "Buying (Long) and Selling (Short)", list: ["Going LONG means you buy expecting the price to rise. You profit if it goes up.", "Going SHORT means you sell first (borrowing the asset) expecting the price to fall, then buy it back cheaper.", "Example: Buy 1 unit at $100, sell at $120 → +$20. If it drops to $90 → -$10."] },
      { h: "Profit and Loss (PnL)", p: ["Every trade ends in a profit, a loss, or breakeven. Professional traders focus on managing losses, not just chasing wins.", "No one wins every trade. What matters is that winners are managed to be larger than losers over time — and even that is never guaranteed."] },
      { h: "Financial markets", p: ["Markets are places where buyers and sellers meet: stock exchanges, forex markets, crypto exchanges and more. Prices move because of supply, demand, news and sentiment."] },
    ],
  },
  {
    id: 2,
    title: "Financial Markets",
    stage: "Fundamentals",
    icon: "Building2",
    minutes: 10,
    summary: "Explore the main markets: stocks, forex, crypto, indices and commodities — neutrally.",
    sections: [
      { h: "Stocks", p: ["A stock is a small share of ownership in a company. If the company grows and is valued more, the stock price can rise.", "Risk: company-specific problems, earnings misses, or wider market crashes can reduce the price."] },
      { h: "Forex", p: ["Forex is the market for trading currencies, always in pairs like EUR/USD.", "The price shows how much of the second currency is needed to buy one unit of the first. Forex is highly liquid and often uses leverage, which increases both potential gains and losses."] },
      { h: "Cryptocurrencies", p: ["Crypto assets (like Bitcoin) are digital assets running on blockchains.", "They can be extremely volatile — large moves up and down are common, which means high risk."] },
      { h: "Indices", p: ["An index tracks a basket of assets, e.g. the S&P 500 (500 large US companies) or Nasdaq 100 (large tech-heavy companies).", "Indices give exposure to a whole market rather than a single company."] },
      { h: "Commodities", p: ["Commodities include gold, silver, oil and agricultural products. Prices are driven by supply, demand, geopolitics and the economy.", "Gold is often seen as a 'safe haven'; oil is sensitive to global events."] },
      { h: "A neutral view", p: ["Every market has advantages and risks. None is a guaranteed way to make money. Understanding the characteristics of each helps you choose what fits your learning goals."] },
    ],
  },
  {
    id: 3,
    title: "Reading Charts",
    stage: "Charts",
    icon: "CandlestickChart",
    minutes: 9,
    summary: "Understand price, time, timeframes, candlesticks and the OHLC values.",
    interactive: "chart",
    sections: [
      { h: "What is a chart?", p: ["A price chart shows how the price of an asset changed over time. The vertical axis is price; the horizontal axis is time."] },
      { h: "Timeframes", list: ["A timeframe is how much time each candle represents.", "1m, 5m, 15m = short term (day trading).", "1h, 4h = swing trading.", "1d, 1w = longer-term view.", "The same asset can look bullish on one timeframe and bearish on another."] },
      { h: "Candlesticks & OHLC", list: ["Each candle summarises price action over its timeframe using four values:", "Open — price at the start of the period.", "High — the highest price reached.", "Low — the lowest price reached.", "Close — price at the end of the period."] },
      { h: "Volume", p: ["Volume shows how much of the asset was traded. Higher volume often means stronger conviction behind a move."] },
      { h: "Try it", p: ["Below the lesson you can open the Interactive Charts page and switch between 1m, 5m, 15m, 1h, 4h, 1d and 1w to see how the same market looks on different timeframes."] },
    ],
  },
  {
    id: 4,
    title: "Candlesticks Patterns",
    stage: "Charts",
    icon: "BarChart3",
    minutes: 9,
    summary: "Anatomy of a candle and common educational patterns.",
    interactive: "candles",
    sections: [
      { h: "Anatomy of a candle", list: ["Body — the range between open and close.", "Wick (shadow) — thin lines showing the high and low.", "A bullish candle closes higher than it opened (green).", "A bearish candle closes lower than it opened (red)."] },
      { h: "What candles tell us", p: ["A long body shows strong momentum. Long wicks show rejection — price tried to move but was pushed back."] },
      { h: "Educational patterns", list: ["Doji — open and close are almost equal; indecision.", "Hammer — small body, long lower wick; possible rejection of lower prices.", "Shooting Star — small body, long upper wick; possible rejection of higher prices.", "Engulfing — a candle whose body fully covers the previous one; possible shift in momentum."] },
      { h: "Important", p: ["No candlestick pattern guarantees the next move. Patterns describe what happened, not what will happen. Always combine them with context and risk management."] },
    ],
  },
  {
    id: 5,
    title: "Market Trends",
    stage: "Analysis",
    icon: "TrendingUp",
    minutes: 8,
    summary: "Uptrends, downtrends, ranges and the structure of highs and lows.",
    interactive: "exercises",
    sections: [
      { h: "The three market states", list: ["Uptrend — price makes higher highs and higher lows.", "Downtrend — price makes lower highs and lower lows.", "Sideways / Range — price moves within a horizontal zone with no clear direction."] },
      { h: "Market structure", list: ["Higher High (HH) — a peak above the previous peak.", "Higher Low (HL) — a dip above the previous dip.", "Lower High (LH) — a peak below the previous peak.", "Lower Low (LL) — a dip below the previous dip."] },
      { h: "Why it matters", p: ["Identifying the trend helps you understand the current context. Many traders prefer to trade in the direction of the larger trend, though trends can change at any time."] },
      { h: "Practice", p: ["Head to the Trend Exercises page and answer: 'What is the trend of this chart?' You'll get instant feedback and earn XP."] },
    ],
  },
  {
    id: 6,
    title: "Support & Resistance",
    stage: "Analysis",
    icon: "AlignHorizontalDistributeCenter",
    minutes: 8,
    summary: "Key price zones, breakouts, retests and false breakouts.",
    sections: [
      { h: "Support", p: ["Support is a price zone where buying interest has historically been strong enough to stop price from falling further."] },
      { h: "Resistance", p: ["Resistance is a price zone where selling interest has historically stopped price from rising further."] },
      { h: "Identifying zones", list: ["Look for prices that were touched and rejected multiple times.", "Zones are areas, not exact lines.", "Old resistance can become new support after a breakout, and vice versa."] },
      { h: "Breakout, retest & false breakout", list: ["Breakout — price moves decisively beyond a zone.", "Retest — price returns to the broken zone to 'test' it before continuing.", "False breakout — price breaks a zone then quickly reverses, trapping traders."] },
      { h: "Important", p: ["Zones show where reactions are more likely, never certain. Always plan for the possibility of a false breakout."] },
    ],
  },
  {
    id: 7,
    title: "Technical Analysis",
    stage: "Analysis",
    icon: "Activity",
    minutes: 12,
    summary: "The main indicators: moving averages, RSI, MACD, Bollinger Bands and volume.",
    interactive: "indicators",
    sections: [
      { h: "Moving Averages (SMA & EMA)", list: ["SMA — Simple Moving Average: the average price over N periods.", "EMA — Exponential Moving Average: gives more weight to recent prices, reacting faster.", "Used to smooth price and identify trend direction. Crossovers between fast and slow averages are watched by many traders."] },
      { h: "RSI (Relative Strength Index)", list: ["Measures the speed and size of recent price moves on a 0–100 scale.", "Above 70 = often called 'overbought'.", "Below 30 = often called 'oversold'.", "Divergence — price makes a new high but RSI does not, hinting momentum is weakening.", "Overbought does not mean price must fall — strong trends can stay overbought."] },
      { h: "MACD", list: ["MACD line — difference between two EMAs.", "Signal line — an EMA of the MACD line.", "Histogram — the gap between MACD and signal.", "A crossover of the MACD above/below the signal line is a common momentum signal."] },
      { h: "Bollinger Bands", list: ["Upper band, lower band and a central moving average.", "Bands widen when volatility rises and narrow when it falls.", "Price near a band shows it is stretched relative to recent average, not a guaranteed reversal."] },
      { h: "Volume", p: ["Volume confirms conviction. A breakout on high volume is generally considered stronger than one on low volume."] },
      { h: "Golden rule", p: ["Indicators are tools, not crystal balls. They are derived from price and can give false signals. Combine them and always manage risk."] },
    ],
  },
  {
    id: 8,
    title: "Price Action",
    stage: "Analysis",
    icon: "Waypoints",
    minutes: 9,
    summary: "Reading raw price: structure, zones, breakouts, retests and rejections.",
    sections: [
      { h: "What is price action?", p: ["Price action is the study of price movement itself, with few or no indicators. It focuses on structure, candles and key zones."] },
      { h: "Core concepts", list: ["Market structure — the sequence of highs and lows defining the trend.", "Support & resistance zones.", "Breakouts and retests.", "Rejections — long wicks showing price was pushed away from a level.", "Price zones — areas where reactions repeatedly occur."] },
      { h: "The honest truth", p: ["No pattern or structure guarantees that price will go up or down. Price action improves your reading of context; it does not predict the future."] },
    ],
  },
  {
    id: 9,
    title: "Trading Strategies",
    stage: "Strategies",
    icon: "Target",
    minutes: 11,
    summary: "How a strategy is built: entry, stop, target, risk and exit.",
    sections: [
      { h: "The anatomy of a strategy", p: ["Every strategy should define clear, repeatable rules so you are not deciding emotionally in the moment:"], list: ["Entry — the exact condition that triggers a trade.", "Stop Loss — where you admit the idea was wrong and exit.", "Take Profit — where you plan to secure gains.", "Risk management — how much you risk per trade.", "Exit — rules for closing, including partial exits."] },
      { h: "Example strategy types (educational)", list: ["Trend-following — enter in the direction of the trend after a pullback.", "Breakout — enter when price breaks a key zone with volume.", "Support/Resistance — buy near support, sell near resistance.", "Moving average — enter on crossovers or bounces off an EMA.", "RSI — look for oversold bounces within an uptrend."] },
      { h: "Critical warning", p: ["None of these strategies is a guarantee of profit. They are frameworks to study and test in simulation. Every strategy will have losing trades."] },
    ],
  },
  {
    id: 10,
    title: "Stop Loss & Take Profit",
    stage: "Strategies",
    icon: "ShieldAlert",
    minutes: 8,
    summary: "Protecting capital and understanding the risk/reward ratio.",
    sections: [
      { h: "Stop Loss (SL)", p: ["A Stop Loss is an order that closes your trade at a predefined price to limit a potential loss. It defines your risk before you enter."] },
      { h: "Take Profit (TP)", p: ["A Take Profit closes your trade at a predefined price to secure gains. It removes the temptation to hold too long out of greed."] },
      { h: "Risk / Reward (R:R)", list: ["Compares what you risk to what you aim to gain.", "Example — Risk: 1, Reward: 2 → you risk $100 to potentially make $200 (1:2).", "With a 1:2 R:R, you can be right less than half the time and still be profitable — but this is never guaranteed."] },
      { h: "Key idea", p: ["Knowing your SL and TP before entering turns trading into a planned process instead of an emotional gamble."] },
    ],
  },
  {
    id: 11,
    title: "Risk Management",
    stage: "Risk Management",
    icon: "Calculator",
    minutes: 12,
    summary: "The most important module: position sizing, drawdown and protecting capital.",
    interactive: "calculator",
    sections: [
      { h: "Why it's the most important skill", p: ["You cannot control whether a single trade wins. You CAN control how much you lose when you are wrong. Risk management is what keeps you in the game."] },
      { h: "How much to risk", list: ["Many traders risk a small fixed percentage per trade (e.g. 1–2% of capital).", "This ensures no single trade can seriously damage your account."] },
      { h: "Position sizing", p: ["Position size is calculated from your risk amount and the distance to your stop loss:", "Position size = (Capital × Risk%) ÷ (Entry − Stop Loss). Use the Risk Calculator to try it."] },
      { h: "Drawdown & losing streaks", list: ["Drawdown is the drop from a peak in your account value.", "Losing streaks are normal — even good systems have them.", "Small risk per trade keeps drawdowns survivable."] },
      { h: "The honest truth", p: ["Risk management reduces the impact of losses — it does NOT eliminate the possibility of losing money. Real trading always carries risk of loss."] },
    ],
  },
  {
    id: 12,
    title: "Trading Psychology",
    stage: "Psychology",
    icon: "Brain",
    minutes: 9,
    summary: "Discipline, patience and the emotions that sabotage traders.",
    sections: [
      { h: "The mental game", p: ["Most trading mistakes are emotional, not technical. Managing yourself is as important as reading charts."] },
      { h: "Emotions to watch", list: ["FOMO — fear of missing out, chasing a move too late.", "Fear — closing winners too early or freezing.", "Greed — holding too long, over-sizing positions.", "Revenge trading — trying to 'win back' a loss immediately.", "Overtrading — taking too many low-quality trades."] },
      { h: "Building discipline", list: ["Follow a written plan with predefined rules.", "Accept that losses are a normal cost of business.", "Be patient — wait for setups that match your rules.", "Review your trades honestly to improve."] },
      { h: "Key idea", p: ["Predefined rules help you act calmly instead of emotionally. Discipline is a skill you build over time."] },
    ],
  },
  {
    id: 13,
    title: "Building a Trading Plan",
    stage: "Strategies",
    icon: "ClipboardList",
    minutes: 7,
    summary: "Create and save your own personal trading plan.",
    interactive: "plan",
    sections: [
      { h: "Why a plan?", p: ["A trading plan turns vague ideas into concrete rules. It keeps you consistent and removes in-the-moment guessing."] },
      { h: "What your plan should include", list: ["Market and timeframe you trade.", "Conditions to enter a trade.", "Stop Loss and Take Profit rules.", "Maximum risk per trade.", "Conditions to exit.", "When NOT to trade (news, tiredness, unclear setups)."] },
      { h: "Save it", p: ["Use the Trading Plan builder below to create your plan. It saves to your profile so you can review and refine it."] },
    ],
  },
  {
    id: 14,
    title: "Backtesting Strategies",
    stage: "Practice",
    icon: "History",
    minutes: 9,
    summary: "Test rules against historical demo data and read the results.",
    interactive: "backtest",
    sections: [
      { h: "What is backtesting?", p: ["Backtesting means applying your trading rules to historical data to see how they would have performed. It helps you study a strategy before risking anything."] },
      { h: "What to measure", list: ["Number of trades.", "Winning trades and losing trades.", "Win rate (%).", "Simulated result (fictional).", "Maximum drawdown."] },
      { h: "Critical warning", p: ["Historical or simulated results do NOT guarantee future results. Markets change. Backtesting is a learning tool, not a promise."] },
      { h: "Practice", p: ["Run the demo backtester on the Backtesting page to see these metrics on historical demo charts."] },
    ],
  },
  {
    id: 15,
    title: "Paper Trading Practice",
    stage: "Practice",
    icon: "Wallet",
    minutes: 10,
    summary: "Put it all together in the $100,000 virtual simulator.",
    interactive: "simulator",
    sections: [
      { h: "What is paper trading?", p: ["Paper trading means practising with virtual money in real-market-style conditions. You build skills and confidence without risking a cent."] },
      { h: "Your virtual account", list: ["You start with $100,000 in virtual paper money.", "Open long and short positions, set Stop Loss and Take Profit.", "Track balance, open positions, PnL, history and performance."] },
      { h: "How to use it well", list: ["Trade the plan you built in Module 13.", "Risk only a small % per trade (Module 11).", "Journal what worked and what didn't."] },
      { h: "Always remember", p: ["This is virtual money — a purely educational simulation. Real trading involves real risk of losing money."] },
    ],
  },
];

export const STAGES = ["Beginner", "Fundamentals", "Charts", "Analysis", "Strategies", "Risk Management", "Psychology", "Practice"];
